import { ConfigProvider } from 'antd'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useRouteMatch } from 'react-router'
import { useGetContextQuery } from '../api/api'
import { useGetEventUserQuery, useLazyGetEventUserQuery } from '../api/eventUser.api'
import { useGetLanguagesQuery } from '../api/v2/languages'
import { Loading } from '../appRouter'
import UserApi from '../services/api/User.api'
import MyLoading from '../components/MyLoading'

const Context = React.createContext({
  event: null,
  eventId: null,
  domainId: null,
  settings: {},
  baseUrl: null,
  user: null,
  userId: null,
})

export const ContextProvider = ({ children }) => {
  const [errorIsExhibitor, setErrorIsExhibitor] = useState(false)
  const [userLoading, setUserLoading] = useState(true)
  const [userId, setUserId] = useState()
  const [user, setUser] = useState()

  const location = useLocation()
  const { context } = useParams()
  const { url: baseUrl } = useRouteMatch()

  const { data } = useGetContextQuery(context)

  const myContext = useMemo(
    () => ({
      eventId: data?.id,
      domainId: data?.domainId,
      userId,
    }),
    [data, userId]
  )

  const contextIsReady = useMemo(() => {
    return !!myContext.eventId && !!myContext.domainId && !!myContext.userId
  }, [myContext])

  const context2IsReady = useMemo(() => {
    return !!myContext.eventId && !!myContext.domainId
  }, [myContext])

  const { data: userUpdated, isFetching: isFetchingUser } = useGetEventUserQuery({ ...myContext }, { skip: !contextIsReady })
  const [fetchUserEvent] = useLazyGetEventUserQuery()
  const { isLoading: isLoadingLanguages } = useGetLanguagesQuery({ ...myContext, userId: undefined }, { skip: !context2IsReady })

  const isLoading = useMemo(() => userLoading || isLoadingLanguages, [isLoadingLanguages, userLoading])

  useEffect(() => {
    if (userUpdated) {
      setUser(userUpdated)
    }
  }, [userUpdated])

  const logout = callback => {
    localStorage.removeItem('token')
    setUserId(null)
    setUser(null)
    if (callback) {
      callback()
    }
  }

  useEffect(() => {
    if (userId) {
      fetchUserEvent({ ...myContext })
        .unwrap()
        .then(u => {
          setUser(u)
          setUserLoading(false)
        })
    }
  }, [fetchUserEvent, myContext, userId])

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      logout()
      setUserLoading(false)
    }

    const u = await new UserApi().getMe().catch(() => null)
    if (u) {
      setUserId(u.id)
    } else {
      // logout()
      setUserLoading(false)
    }
  }, [])

  const login = async newToken => {
    localStorage.setItem('token', newToken)
    window.location.reload()
    // await fetchUser()
  }

  useEffect(() => {
    if (data) {
      localStorage.setItem('eventId', data.id)
      localStorage.setItem('domainId', data.domainId)

      const search = new URLSearchParams(location.search)
      const newToken = search.get('token')
      if (newToken) {
        localStorage.setItem('token', newToken)
      }

      const referer = search.get('referer')
      if (referer) {
        window.location.href = referer
      } else {
        fetchUser()
      }
    }
  }, [data, fetchUser, location.search])

  useEffect(() => {
    if (user?.UserEvent?.isExhibitor) {
      setErrorIsExhibitor(true)
      // logout()
    }
  }, [user])

  if (isLoading) {
    return <MyLoading loading />
  }

  if (!data) {
    return <div className="has-text-centered">Pour accéder sur cette page, vous devez être dans le contexte d'un événement</div>
  }

  return (
    <Context.Provider
      value={{
        event: data,
        eventId: data?.id,
        domainId: data?.domainId,
        myContext: {
          eventId: data?.id,
          domainId: data?.domainId,
          userId: user?.id,
        },
        baseUrl,
        user,
        userId,
        // Vrai tant que le eventUser se re-fetch (ex. après un changement de typo qui invalide
        // EVENT_USER). Sert à geler les auto-sauts d'étapes tant que la nav peut encore changer
        // de forme — voir Steps.jsx (settling) et Dates.jsx.
        isFetchingUser,
        logout,
        login,
        errorIsExhibitor,
      }}
    >
      <ConfigProvider>{children}</ConfigProvider>
    </Context.Provider>
  )
}

export const useMyContext = () => useContext(Context)
